import { useEffect, useRef } from 'react';

import { get } from 'lodash';

import { useCurrentUser } from '@lib/hooks';

import { subscribeSocketEvents, unsubscribeSocketEvents } from 'utils/socket';

import useLiveRef from './useLiveRef';

type SetTimeoutReturn = ReturnType<typeof setTimeout>;

/**
 * Subscribe to socket events data and debounce the callback to handle changes at once
 *
 * @param socketEvents Event names we want to subscribe to
 * @param cb Callback to handle the array of datas
 * @param delay
 */
const useSocketEventsDebounced = (
  socketEvents: string[],
  cb: (ids: any[]) => void,
  delay: number | undefined = 500,
) => {
  const currentUser = useCurrentUser();
  const array = useRef<any[]>([]);
  const timerRef = useRef<SetTimeoutReturn>();
  const isSubscribed = useRef<boolean>(false);
  // Avoid passing unstable reference values into
  // `useEffect` dependency array.
  const onSocketCallback = useLiveRef(cb);
  const socketEventsString = socketEvents.join(',');
  const currentUserId = get(currentUser, '_id');
  const isWidgetUser = get(currentUser, '$isWidgetUser');

  useEffect(
    () => {
      if (!currentUserId || isWidgetUser || isSubscribed.current) {
        return;
      }
      const $socketEvents = socketEventsString ? socketEventsString.split(',') : [];
      const onSocket = (data: any = {}, ackCallback?: Function) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        array.current.push(data);

        timerRef.current = setTimeout(() => {
          onSocketCallback.current([...array.current]);
          array.current.splice(0, array.current.length);
        }, delay);

        ackCallback?.();
      };
      isSubscribed.current = subscribeSocketEvents($socketEvents, onSocket);
      // WORKAROUND: Retry since we might be trying to subscribe before the socket is ready
      if (!isSubscribed.current) {
        setTimeout(() => {
          isSubscribed.current = subscribeSocketEvents($socketEvents, onSocket);
        }, 5000);
      }
      return () => {
        unsubscribeSocketEvents($socketEvents, onSocket);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserId, isWidgetUser, socketEventsString, delay],
  );
};

export default useSocketEventsDebounced;
